import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, InputGroup, Alert, Spinner, Modal, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { 
  FaSearch, FaPlus, FaEdit, FaTrash, FaFilter, FaSave, FaTimes, 
  FaMoneyBillWave, FaArrowUp, FaArrowDown, FaCheck, FaExclamationTriangle,
  FaCalendarAlt, FaSort, FaSortUp, FaSortDown, FaDownload, FaEye, FaFileInvoice,
  FaUndo, FaClock
} from 'react-icons/fa';

// import { db } from "../../services/firebaseConnection";
import { useFirebaseConnection } from "../../services/useFirebaseConnection";
import {
  collection, getDocs, doc, updateDoc, deleteDoc, addDoc
} from "firebase/firestore";

// Constantes
const TIPOS_CONTA = {
  RECEBER: 'receber',
  PAGAR: 'pagar'
};

const STATUS_CONTA = {
  PENDENTE: 'pendente',
  PAGO: 'pago',
  ATRASADO: 'atrasado'
};

const FILTROS_TIPO = {
  TODOS: 'todos',
  RECEBER: 'receber',
  PAGAR: 'pagar'
};

// Função para formatar moeda
const formatCurrency = (value) => {
  if (typeof value !== 'number' || isNaN(value)) return 'R$ 0,00';
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
};

// Função para validar data
const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

// Função para verificar se a conta está atrasada
const isOverdue = (dataVencimento, status) => {
  if (status === STATUS_CONTA.PAGO) return false;
  if (!dataVencimento) return false;
  
  const hoje = new Date();
  const vencimento = new Date(dataVencimento);
  return vencimento < hoje;
};

// Função para formatar data
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('pt-BR');
  } catch {
    return '-';
  }
};

// Hook customizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function Contas() {
  const [contas, setContas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState(FILTROS_TIPO.TODOS);
  const [filterStatus, setFilterStatus] = useState("todos");
  const [showForm, setShowForm] = useState(false);
  const [currentConta, setCurrentConta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contaToDelete, setContaToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [parcelaToToggle, setParcelaToToggle] = useState(null);
  const { db } = useFirebaseConnection();

  // Debounce do termo de busca para melhor performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Carregar dados do Firestore
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      // Pega as contas da coleção 'contas'
      const contasSnapshot = await getDocs(collection(db, "contas"));
      const contasData = contasSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        valor: Number(doc.data().valor) || 0
      }));

      // Pega os pedidos e transforma as parcelas em contas a receber individuais
      const pedidosSnapshot = await getDocs(collection(db, "pedidos"));
      const parcelasData = [];
      
      pedidosSnapshot.docs.forEach(doc => {
        const pedidoData = doc.data();
        const pedidoId = doc.id;
        
        // Verifica se o pedido tem parcelas
        if (pedidoData.parcelas && Array.isArray(pedidoData.parcelas)) {
          pedidoData.parcelas.forEach((parcela, index) => {
            // Cria uma conta a receber para cada parcela
            const parcelaId = `parcela_${pedidoId}_${parcela.numero || (index + 1)}`;
            const totalParcelas = pedidoData.parcelas.length;
            
            parcelasData.push({
              id: parcelaId,
              tipo: TIPOS_CONTA.RECEBER,
              descricao: `${pedidoData.clienteNome || 'Cliente'} - Parcela ${parcela.numero || (index + 1)}/${totalParcelas}`,
              categoria: "Parcela de Pedido",
              valor: Number(parcela.valor) || 0,
              dataVencimento: parcela.dataVencimento || "",
              dataPagamento: parcela.pago ? (parcela.dataPagamento || new Date().toISOString().split('T')[0]) : "",
              status: parcela.pago ? STATUS_CONTA.PAGO : STATUS_CONTA.PENDENTE,
              isParcela: true,
              pedidoId: pedidoId,
              numeroParcela: parcela.numero || (index + 1),
              clienteNome: pedidoData.clienteNome || 'Cliente',
              clienteId: pedidoData.clienteId || '',
              parcelaOriginal: parcela // Mantém referência à parcela original
            });
          });
        }
      });

      // Junta as contas + parcelas dos pedidos
      const todasContas = [...contasData, ...parcelasData];
      
      // Atualiza status para atrasado se necessário
      const contasComStatus = todasContas.map(conta => ({
        ...conta,
        status: isOverdue(conta.dataVencimento, conta.status) ? STATUS_CONTA.ATRASADO : conta.status
      }));
      
      setContas(contasComStatus);
    } catch (error) {
      console.error("Erro ao carregar contas:", error);
      setError("Erro ao carregar contas. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrar e ordenar contas com useMemo para otimização
  const filteredAndSortedContas = useMemo(() => {
    let filtered = contas;

    // Filtro por tipo
    if (filterTipo !== FILTROS_TIPO.TODOS) {
      filtered = filtered.filter(c => c.tipo === filterTipo);
    }

    // Filtro por status
    if (filterStatus !== "todos") {
      filtered = filtered.filter(c => c.status === filterStatus);
    }

    // Filtro por busca
    if (debouncedSearchTerm.trim() !== "") {
      const term = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        (c.descricao || "").toLowerCase().includes(term) ||
        (c.categoria || "").toLowerCase().includes(term) ||
        (c.clienteNome || "").toLowerCase().includes(term)
      );
    }

    // Ordenação
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Tratamento especial para valores numéricos
        if (sortConfig.key === 'valor') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        // Tratamento especial para datas
        if (sortConfig.key === 'dataVencimento' || sortConfig.key === 'dataPagamento') {
          aValue = aValue ? new Date(aValue) : new Date(0);
          bValue = bValue ? new Date(bValue) : new Date(0);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [contas, filterTipo, filterStatus, debouncedSearchTerm, sortConfig]);

  // Calcular totais com useMemo para otimização
  const totals = useMemo(() => {
    const totalReceber = contas
      .filter(c => c.tipo === TIPOS_CONTA.RECEBER && c.status === STATUS_CONTA.PENDENTE)
      .reduce((sum, conta) => sum + (conta.valor || 0), 0);
      
    const totalPagar = contas
      .filter(c => c.tipo === TIPOS_CONTA.PAGAR && c.status === STATUS_CONTA.PENDENTE)
      .reduce((sum, conta) => sum + (conta.valor || 0), 0);

    const totalAtrasado = contas
      .filter(c => c.status === STATUS_CONTA.ATRASADO)
      .reduce((sum, conta) => sum + (conta.valor || 0), 0);

    const saldoPrevisto = totalReceber - totalPagar;

    return { totalReceber, totalPagar, totalAtrasado, saldoPrevisto };
  }, [contas]);

  // Função para ordenar
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Confirmar alteração de status de pagamento
  const confirmTogglePayment = useCallback((conta) => {
    setParcelaToToggle(conta);
    setShowPaymentModal(true);
  }, []);

  // Alternar status de pagamento da parcela
  const handleTogglePayment = useCallback(async (markAsPaid = true) => {
    if (!parcelaToToggle) return;
    
    try {
      setError("");
      setShowPaymentModal(false);
      
      const conta = parcelaToToggle;
      const dataPagamento = markAsPaid ? new Date().toISOString().split('T')[0] : "";
      
      if (conta.id.startsWith("parcela_")) {
        // Extrair pedidoId e numeroParcela do ID da parcela
        const [, pedidoId, numeroParcela] = conta.id.split("_");
        
        // Buscar o pedido no Firestore
        const pedidosSnapshot = await getDocs(collection(db, "pedidos"));
        const pedidoDoc = pedidosSnapshot.docs.find(doc => doc.id === pedidoId);
        
        if (pedidoDoc) {
          const pedidoData = pedidoDoc.data();
          const parcelas = [...(pedidoData.parcelas || [])];
          
          // Encontrar e atualizar a parcela específica
          const parcelaIndex = parcelas.findIndex(p => 
            (p.numero || 0).toString() === numeroParcela.toString()
          );
          
          if (parcelaIndex !== -1) {
            parcelas[parcelaIndex] = {
              ...parcelas[parcelaIndex],
              pago: markAsPaid,
              dataPagamento: dataPagamento
            };
            
            // Atualizar o pedido no Firestore
            const pedidoRef = doc(db, "pedidos", pedidoId);
            await updateDoc(pedidoRef, { parcelas });
          }
        }
      } else {
        // Conta normal
        const contaRef = doc(db, "contas", conta.id);
        await updateDoc(contaRef, {
          status: markAsPaid ? STATUS_CONTA.PAGO : STATUS_CONTA.PENDENTE,
          dataPagamento
        });
      }
      
      // Atualiza localmente
      setContas(prev =>
        prev.map(c =>
          c.id === conta.id ? { 
            ...c, 
            status: markAsPaid ? STATUS_CONTA.PAGO : STATUS_CONTA.PENDENTE, 
            dataPagamento 
          } : c
        )
      );
      
      setParcelaToToggle(null);
    } catch (error) {
      console.error("Erro ao alterar status de pagamento:", error);
      setError("Erro ao alterar status de pagamento. Tente novamente.");
      setParcelaToToggle(null);
    }
  }, [parcelaToToggle]);

  // Confirmar exclusão
  const confirmDelete = useCallback((conta) => {
    if (conta.isParcela) {
      setError("Não é permitido excluir parcelas de pedidos. Gerencie através do pedido original.");
      return;
    }
    setContaToDelete(conta);
    setShowDeleteModal(true);
  }, []);

  // Excluir conta
  const handleDeleteConta = useCallback(async () => {
    if (!contaToDelete) return;
    
    try {
      setError("");
      await deleteDoc(doc(db, "contas", contaToDelete.id));
      setContas(prev => prev.filter(c => c.id !== contaToDelete.id));
      setShowDeleteModal(false);
      setContaToDelete(null);
    } catch (error) {
      console.error("Erro ao excluir conta:", error);
      setError("Erro ao excluir conta. Tente novamente.");
    }
  }, [contaToDelete]);

  // Abrir formulário para editar
  const handleEditConta = useCallback((conta) => {
    if (conta.isParcela) {
      setError("Edição não disponível para parcelas de pedidos. Gerencie através do pedido original.");
      return;
    }
    setCurrentConta(conta);
    setShowForm(true);
  }, []);

  // Salvar conta
  const handleSaveConta = useCallback(async (contaData) => {
    try {
      setError("");
      
      const dataToSave = {
        tipo: contaData.tipo,
        descricao: contaData.descricao,
        categoria: contaData.categoria,
        valor: Number(contaData.valor),
        dataVencimento: contaData.dataVencimento,
        dataPagamento: contaData.dataPagamento,
        status: contaData.status
      };
      
      if (contaData.id) {
        // Editar conta existente
        const contaRef = doc(db, "contas", contaData.id);
        await updateDoc(contaRef, dataToSave);
        
        setContas(prev =>
          prev.map(c => (c.id === contaData.id ? { ...contaData, valor: Number(contaData.valor) } : c))
        );
      } else {
        // Criar nova conta
        const docRef = await addDoc(collection(db, "contas"), dataToSave);
        
        const novaConta = {
          id: docRef.id,
          ...contaData,
          valor: Number(contaData.valor)
        };
        
        setContas(prev => [...prev, novaConta]);
      }
      
      setShowForm(false);
      setCurrentConta(null);
    } catch (error) {
      console.error("Erro ao salvar conta:", error);
      setError("Erro ao salvar conta. Tente novamente.");
    }
  }, []);

  // Exportar dados para CSV
  const exportToCSV = useCallback(() => {
    const headers = ['Descrição', 'Categoria', 'Tipo', 'Valor', 'Vencimento', 'Status', 'Data Pagamento', 'Cliente'];
    const csvContent = [
      headers.join(','),
      ...filteredAndSortedContas.map(conta => [
        `"${conta.descricao}"`,
        `"${conta.categoria}"`,
        conta.tipo === TIPOS_CONTA.RECEBER ? 'A Receber' : 'A Pagar',
        conta.valor,
        formatDate(conta.dataVencimento),
        conta.status === STATUS_CONTA.PAGO ? 'Pago' : 
        conta.status === STATUS_CONTA.ATRASADO ? 'Atrasado' : 'Pendente',
        formatDate(conta.dataPagamento),
        `"${conta.clienteNome || '-'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `contas_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredAndSortedContas]);

  // Renderizar ícone de ordenação
  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'asc' ? 
      <FaSortUp className="ms-1" /> : 
      <FaSortDown className="ms-1" />;
  };

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
        <p className="mt-2">Carregando contas...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Contas a Pagar e Receber</h1>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-secondary" 
            onClick={exportToCSV}
            disabled={filteredAndSortedContas.length === 0}
          >
            <FaDownload className="me-2" /> Exportar CSV
          </Button>
          <Button 
            variant="primary" 
            onClick={() => { setCurrentConta(null); setShowForm(true); }}
          >
            <FaPlus className="me-2" /> Nova Conta
          </Button>
        </div>
      </div>

      {/* Alerta de erro */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          <FaExclamationTriangle className="me-2" />
          {error}
        </Alert>
      )}
      
      {/* Cards de resumo */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">A Receber</div>
                  <h4 className="mb-0 text-success">{formatCurrency(totals.totalReceber)}</h4>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                  <FaArrowDown className="text-success" size={20} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">A Pagar</div>
                  <h4 className="mb-0 text-danger">{formatCurrency(totals.totalPagar)}</h4>
                </div>
                <div className="bg-danger bg-opacity-10 p-3 rounded-circle">
                  <FaArrowUp className="text-danger" size={20} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">Em Atraso</div>
                  <h4 className="mb-0 text-warning">{formatCurrency(totals.totalAtrasado)}</h4>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded-circle">
                  <FaCalendarAlt className="text-warning" size={20} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">Saldo Previsto</div>
                  <h4 className={`mb-0 ${totals.saldoPrevisto >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatCurrency(totals.saldoPrevisto)}
                  </h4>
                </div>
                <div className={`${totals.saldoPrevisto >= 0 ? 'bg-success' : 'bg-danger'} bg-opacity-10 p-3 rounded-circle`}>
                  <FaMoneyBillWave className={`${totals.saldoPrevisto >= 0 ? 'text-success' : 'text-danger'}`} size={20} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Barra de pesquisa e filtros */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row>
            <Col lg={4} md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por descrição, categoria ou cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col lg={8} md={6} className="d-flex justify-content-end gap-2 mt-3 mt-md-0 flex-wrap">
              <Form.Select
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value)}
                className="w-auto"
              >
                <option value={FILTROS_TIPO.TODOS}>Todos os Tipos</option>
                <option value={TIPOS_CONTA.RECEBER}>A Receber</option>
                <option value={TIPOS_CONTA.PAGAR}>A Pagar</option>
              </Form.Select>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-auto"
              >
                <option value="todos">Todos os Status</option>
                <option value={STATUS_CONTA.PENDENTE}>Pendente</option>
                <option value={STATUS_CONTA.PAGO}>Pago</option>
                <option value={STATUS_CONTA.ATRASADO}>Atrasado</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Formulário de conta */}
      {showForm && (
        <ContaForm 
          conta={currentConta} 
          onSave={handleSaveConta} 
          onCancel={() => { setShowForm(false); setCurrentConta(null); setError(""); }} 
        />
      )}
      
      {/* Tabela de contas */}
      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              {filteredAndSortedContas.length} conta(s) encontrada(s)
            </h5>
          </div>
          <div className="table-responsive">
            <Table hover>
              <thead>
                <tr>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('descricao')}
                  >
                    Descrição {renderSortIcon('descricao')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('categoria')}
                  >
                    Categoria {renderSortIcon('categoria')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('tipo')}
                  >
                    Tipo {renderSortIcon('tipo')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('valor')}
                  >
                    Valor {renderSortIcon('valor')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('dataVencimento')}
                  >
                    Vencimento {renderSortIcon('dataVencimento')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('status')}
                  >
                    Status {renderSortIcon('status')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('clienteNome')}
                  >
                    Cliente {renderSortIcon('clienteNome')}
                  </th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedContas.map((conta) => (
                  <tr key={conta.id}>
                    <td>
                      <div>
                        {conta.descricao}
                        {conta.isParcela && (
                          <div bg="info" className="ms-2" size="sm">
                            <FaFileInvoice className="me-1" />
                            Parcela
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{conta.categoria}</td>
                    <td>
                      <div bg={conta.tipo === TIPOS_CONTA.RECEBER ? 'success' : 'danger'}>
                        {conta.tipo === TIPOS_CONTA.RECEBER ? 'A Receber' : 'A Pagar'}
                      </div>
                    </td>
                    <td className="fw-bold">{formatCurrency(conta.valor)}</td>
                    <td>{formatDate(conta.dataVencimento)}</td>
                    <td>
                      <div bg={
                        conta.status === STATUS_CONTA.PAGO ? 'success' :
                        conta.status === STATUS_CONTA.ATRASADO ? 'danger' :
                        'warning'
                      }>
                        {conta.status === STATUS_CONTA.PAGO ? 'Pago' : 
                         conta.status === STATUS_CONTA.ATRASADO ? 'Atrasado' : 'Pendente'}
                      </div>
                    </td>
                    <td>{conta.clienteNome || '-'}</td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-1">
                        {/* Botão para marcar/desmarcar como pago */}
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>
                            {conta.status === STATUS_CONTA.PAGO ? 'Marcar como pendente' : 'Marcar como pago'}
                          </Tooltip>}
                        >
                          <Button 
                            variant={conta.status === STATUS_CONTA.PAGO ? "outline-warning" : "outline-success"}
                            size="sm"
                            onClick={() => confirmTogglePayment(conta)}
                          >
                            {conta.status === STATUS_CONTA.PAGO ? <FaUndo /> : <FaCheck />}
                          </Button>
                        </OverlayTrigger>
                        
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{conta.isParcela ? "Não é possível editar parcelas de pedidos" : "Editar conta"}</Tooltip>}
                        >
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => handleEditConta(conta)}
                            disabled={conta.isParcela}
                          >
                            <FaEdit />
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{conta.isParcela ? "Não é possível excluir parcelas de pedidos" : "Excluir conta"}</Tooltip>}
                        >
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => confirmDelete(conta)}
                            disabled={conta.isParcela}
                          >
                            <FaTrash />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredAndSortedContas.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      <div className="text-muted">
                        <FaEye size={48} className="mb-3 opacity-50" />
                        <p className="mb-0">Nenhuma conta encontrada</p>
                        <small>Tente ajustar os filtros ou adicionar uma nova conta</small>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Modal de confirmação de alteração de pagamento */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Alteração</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FaClock size={48} className="text-primary mb-3" />
            <p>Deseja alterar o status de pagamento da conta:</p>
            <strong>"{parcelaToToggle?.descricao}"</strong>
            <p className="mt-3">
              Status atual: <div bg={
                parcelaToToggle?.status === STATUS_CONTA.PAGO ? 'success' : 'warning'
              }>
                {parcelaToToggle?.status === STATUS_CONTA.PAGO ? 'Pago' : 'Pendente'}
              </div>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
            Cancelar
          </Button>
          {parcelaToToggle?.status === STATUS_CONTA.PAGO ? (
            <Button variant="warning" onClick={() => handleTogglePayment(false)}>
              <FaUndo className="me-2" />
              Marcar como Pendente
            </Button>
          ) : (
            <Button variant="success" onClick={() => handleTogglePayment(true)}>
              <FaCheck className="me-2" />
              Marcar como Pago
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmação de exclusão */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FaExclamationTriangle size={48} className="text-warning mb-3" />
            <p>Tem certeza que deseja excluir a conta:</p>
            <strong>"{contaToDelete?.descricao}"</strong>
            <p className="mt-2 text-muted small">Esta ação não pode ser desfeita.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteConta}>
            <FaTrash className="me-2" />
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

// Componente de formulário para adicionar/editar conta (mantido igual)
function ContaForm({ conta, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    id: conta?.id || null,
    tipo: conta?.tipo || TIPOS_CONTA.RECEBER,
    descricao: conta?.descricao || '',
    valor: conta?.valor || '',
    dataVencimento: conta?.dataVencimento || '',
    dataPagamento: conta?.dataPagamento || '',
    status: conta?.status || STATUS_CONTA.PENDENTE,
    categoria: conta?.categoria || ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.categoria.trim()) {
      newErrors.categoria = 'Categoria é obrigatória';
    }

    if (!formData.valor || Number(formData.valor) <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (formData.dataVencimento && !isValidDate(formData.dataVencimento)) {
      newErrors.dataVencimento = 'Data de vencimento inválida';
    }

    if (formData.dataPagamento && !isValidDate(formData.dataPagamento)) {
      newErrors.dataPagamento = 'Data de pagamento inválida';
    }

    // Se status é pago, data de pagamento é obrigatória
    if (formData.status === STATUS_CONTA.PAGO && !formData.dataPagamento) {
      newErrors.dataPagamento = 'Data de pagamento é obrigatória para contas pagas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);
  
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Erro ao salvar:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, onSave]);

  // Atualizar data de pagamento automaticamente quando status muda para pago
  useEffect(() => {
    if (formData.status === STATUS_CONTA.PAGO && !formData.dataPagamento) {
      setFormData(prev => ({
        ...prev,
        dataPagamento: new Date().toISOString().split('T')[0]
      }));
    }
  }, [formData.status, formData.dataPagamento]);
  
  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header as="h5" className="bg-primary text-white">
        <div className="d-flex align-items-center">
          <FaPlus className="me-2" />
          {conta ? 'Editar Conta' : 'Nova Conta'}
        </div>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo *</Form.Label>
                <Form.Select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.tipo}
                >
                  <option value={TIPOS_CONTA.RECEBER}>A Receber</option>
                  <option value={TIPOS_CONTA.PAGAR}>A Pagar</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.tipo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Categoria *</Form.Label>
                <Form.Control
                  type="text"
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.categoria}
                  placeholder="Ex: Fornecedores, Clientes, Impostos..."
                />
                <Form.Control.Feedback type="invalid">
                  {errors.categoria}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Descrição *</Form.Label>
                <Form.Control
                  type="text"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.descricao}
                  placeholder="Descreva a conta..."
                />
                <Form.Control.Feedback type="invalid">
                  {errors.descricao}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Valor *</Form.Label>
                <InputGroup>
                  <InputGroup.Text>R$</InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="valor"
                    value={formData.valor}
                    onChange={handleChange}
                    required
                    min="0.01"
                    step="0.01"
                    isInvalid={!!errors.valor}
                    placeholder="0,00"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.valor}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Data de Vencimento</Form.Label>
                <Form.Control
                  type="date"
                  name="dataVencimento"
                  value={formData.dataVencimento}
                  onChange={handleChange}
                  isInvalid={!!errors.dataVencimento}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.dataVencimento}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value={STATUS_CONTA.PENDENTE}>Pendente</option>
                  <option value={STATUS_CONTA.PAGO}>Pago</option>
                  <option value={STATUS_CONTA.ATRASADO}>Atrasado</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {formData.status === STATUS_CONTA.PAGO && (
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Data de Pagamento *</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataPagamento"
                    value={formData.dataPagamento}
                    onChange={handleChange}
                    isInvalid={!!errors.dataPagamento}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.dataPagamento}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
          </Row>
          
          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button 
              variant="secondary" 
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <FaTimes className="me-2" /> Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Salvando...
                </>
              ) : (
                <>
                  <FaSave className="me-2" /> Salvar
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default Contas;

